import { useState } from 'react';
import { Attachment } from '../../libs/models/ChatMessage';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { Info16Regular, ChevronDown16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@fluentui/react-components';

interface CalculationGuideButtonProps {
    attachment: Attachment;
    className?: string;
}

const useStyles = makeStyles({
    container: {
        width: '100%',
        marginBottom: '3px',
        position: 'relative',
    },
    buttonBase: {
        width: '100%',
        minHeight: '20px',
        padding: '5px',
        border: `1px solid ${tokens.colorPaletteBlueBorderActive}`,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground1,
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground2,
        },
    },
    buttonOpen: {
        backgroundColor: tokens.colorPaletteBlueBackground2,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)',
    },
    buttonClosed: {
        backgroundColor: tokens.colorPaletteBlueBackground2,
        ':hover': {
            backgroundColor: tokens.colorPaletteBlueBackground2,
        },
        border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    buttonContent: {
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        width: '100%',
        '@media (max-width: 768px)': {
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
    },
    titleSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        flexShrink: 0,
        color: tokens.colorPaletteBlueForeground2,
    },
    icon: {
        height: '20px',
        width: '20px',
        color: tokens.colorPaletteBlueForeground2,
    },
    title: {
        fontWeight: tokens.fontWeightSemibold,
    },
    summary: {
        fontStyle: 'italic',
        color: tokens.colorPaletteBlueForeground2,
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        paddingRight: '90px',
        '@media (max-width: 768px)': {
            width: '100%',
            paddingLeft: 0,
            paddingRight: '5px',
            whiteSpace: 'normal',
            marginBottom: '5px',
        },
    },
    clickPrompt: {
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        color: tokens.colorPaletteBlueForeground2,
        marginLeft: 'auto',
        flexShrink: 0,
        width: '85px',
        '@media (max-width: 768px)': {
            width: '100%',
            justifyContent: 'flex-end',
            paddingLeft: 0,
            paddingRight: '5px',
        },
    },
    chevron: {
        height: '18px',
        width: '18px',
    },
    overlay: {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 39,
    },
    modal: {
        position: 'absolute',
        top: '100%',
        marginTop: '2px',
        left: 0,
        right: 0,
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusMedium,
        boxShadow: tokens.shadow8,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        maxWidth: '400px',
        zIndex: 40,
    },
    modalContent: {
        padding: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    modalTitle: {
        fontWeight: tokens.fontWeightMedium,
        color: tokens.colorNeutralForeground1,
    },
    calculationsContainer: {
        marginTop: '5px',
        gap: '8px',
    },
    sectionContainer: {
        gap: '5px',
    },
    sectionTitle: {
        fontWeight: tokens.fontWeightMedium,
        color: tokens.colorNeutralForeground2,
    },
    actionsList: {
        listStyleType: 'disc',
        paddingLeft: '10px',
        gap: '3px',
    },
    actionItem: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground3,
    },
    prerequisitesSection: {
        paddingTop: '5px',
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    prerequisitesTitle: {
        fontWeight: tokens.fontWeightMedium,
        color: tokens.colorNeutralForeground2,
        marginBottom: '3px',
    },
    prerequisitesList: {
        listStyleType: 'disc',
        paddingLeft: '10px',
        gap: '3px',
    },
    note: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground3,
        fontStyle: 'italic',
    },
    skeletonContainer: {
        width: '100%',
        marginBottom: '3px',
    },
    skeletonContent: {
        padding: '5px',
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground1,
        width: '100%',
        '@media (max-width: 768px)': {
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
    },
    skeletonTitleSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        flexShrink: 0,
    },
    skeletonSummary: {
        flex: 1,
        paddingRight: '90px',
        '@media (max-width: 768px)': {
            width: '100%',
            paddingRight: '5px',
        },
    },
    skeletonClickPrompt: {
        marginLeft: 'auto',
        width: '85px',
        flexShrink: 0,
        '@media (max-width: 768px)': {
            width: '100%',
            justifyContent: 'flex-end',
        },
    },
    skeletonSummaryContent: {
        display: 'flex',
        alignItems: 'center',
    },
    skeletonDash: {
        marginRight: '4px',
        color: tokens.colorNeutralForeground4,
    },
    skeletonFlex: {
        flex: 1,
    },
    skeletonClickPromptWidth: {
        width: '80px',
    },
});

export default function CalculationGuideButton({ attachment, className = '' }: CalculationGuideButtonProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const classes = useStyles();
    const { t } = useTranslation();

    if (attachment.creatingExplanation && !attachment.explanation) {
        return (
            <div className={mergeClasses(classes.container, className)}>
                <button className={mergeClasses(classes.buttonBase, classes.buttonClosed)}>
                    <div className={classes.buttonContent}>
                        <div className={classes.titleSection}>
                            <Info16Regular className={classes.icon} />
                            <Skeleton />
                        </div>
                        <span className={mergeClasses(classes.summary, classes.skeletonSummaryContent)}>
                            <span className={classes.skeletonDash}>—</span>
                            <Skeleton className={classes.skeletonFlex} />
                        </span>
                        <div className={classes.clickPrompt}>
                            <ChevronDown16Regular className={classes.chevron} />
                            <Skeleton className={classes.skeletonClickPromptWidth} />
                        </div>
                    </div>
                </button>
            </div>
        );
    }

    if (!attachment.explanation) {
        return null;
    }

    const guide = attachment.explanation;

    return (
        <div className={mergeClasses(classes.container, className)}>
            <button
                onClick={() => {
                    setIsModalOpen(!isModalOpen);
                }}
                className={mergeClasses(classes.buttonBase, isModalOpen ? classes.buttonOpen : classes.buttonClosed)}
            >
                <div className={classes.buttonContent}>
                    <div className={classes.titleSection}>
                        <Info16Regular className={classes.icon} />
                        <span className={classes.title}>{t('calculationGuide.understandingResults')}</span>
                    </div>
                    <span className={classes.summary}>— {guide.summary}</span>
                    <div className={classes.clickPrompt}>
                        <ChevronDown16Regular className={classes.chevron} />
                        <span>{t('calculationGuide.clickForDetails')}</span>
                    </div>
                </div>
            </button>
            {isModalOpen && (
                <>
                    <div
                        className={classes.overlay}
                        onClick={() => {
                            setIsModalOpen(false);
                        }}
                    />
                    <div className={classes.modal}>
                        <div className={classes.modalContent}>
                            <div>
                                <h3 className={classes.modalTitle}>{guide.title}</h3>
                                <div className={classes.calculationsContainer}>
                                    {guide.calculations.map((section, idx) => (
                                        <div key={idx} className={classes.sectionContainer}>
                                            <h4 className={classes.sectionTitle}>{section.title}</h4>
                                            <ul className={classes.actionsList}>
                                                {section.actions.map((action, actionIdx) => (
                                                    <li key={actionIdx} className={classes.actionItem}>
                                                        {action}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {guide.note && (
                                <div className={classes.note}>
                                    {t('calculationGuide.note')}: {guide.note}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
